import { useEffect, useState, useCallback } from 'react'
import { apiFixtureList, apiLeagueTable, apiMatchStartLive, apiRoundAdvance } from '../server/ServiceCall'
import { AuthState } from '../store/auth'
import { FixtureRow } from '../store/club'

interface StandingRow {
  CLUB_CODE:    number
  CLUB_NAME:    string
  USER_CODE:    number
  WIN_CNT:      number
  DRAW_CNT:     number
  LOSE_CNT:     number
  GOAL_FOR:     number
  GOAL_AGAINST: number
  GOAL_DIFF:    number
  POINTS:       number
  PLAYED:       number
}

interface SeasonInfo {
  LEAGUE_NAME:   string
  SEASON_NO:     number
  CURRENT_ROUND: number
  ROUND_TOTAL:   number
  SEASON_START:  string
}

interface Props {
  auth:        AuthState
  leagueCode:  number
  seasonNo:    number
  myClubCode:  number
  roundTotal:  number
  onGoMatch:   (fixtureCode: number) => void
}

type Tab = 'fixtures' | 'table'

export default function Fixtures({ auth, leagueCode, seasonNo, myClubCode, roundTotal, onGoMatch }: Props) {
  const [tab,        setTab]        = useState<Tab>('fixtures')
  const [round,      setRound]      = useState(1)
  const [fixtures,   setFixtures]   = useState<FixtureRow[]>([])
  const [standings,  setStandings]  = useState<StandingRow[]>([])
  const [season,     setSeason]     = useState<SeasonInfo | null>(null)
  const [loading,    setLoading]    = useState(false)
  const [err,        setErr]        = useState('')
  const [actionMsg,  setActionMsg]  = useState<{ text: string, ok: boolean } | null>(null)

  const fetchFixtures = useCallback(() => {
    setLoading(true); setErr('')
    apiFixtureList(auth.token, leagueCode, seasonNo, round, round + 4).then(r => {
      if (r.resultCode !== '00') { setErr(r.resultMessage || '조회 실패'); setLoading(false); return }
      try {
        const obj = JSON.parse(r.resultData)
        setFixtures((obj.fixtures ?? []) as FixtureRow[])
      } catch { setErr('응답 파싱 실패') } finally { setLoading(false) }
    }).catch(e => { setErr(e?.message ?? '네트워크 오류'); setLoading(false) })
  }, [auth.token, leagueCode, seasonNo, round])

  async function handleStartLive(fc: number) {
    setActionMsg(null)
    const r = await apiMatchStartLive(auth.token, fc)
    if (r.resultCode !== '00') {
      setActionMsg({ text: r.resultMessage || '시작 실패', ok: false })
      return
    }
    onGoMatch(fc)
  }

  async function handleAdvanceRound(rn: number) {
    if (!window.confirm(`Round ${rn} 모든 미진행 경기를 일괄 시뮬합니다. 진행할까요?`)) return
    setActionMsg({ text: '일괄 시뮬 중…', ok: true })
    const r = await apiRoundAdvance(auth.token, leagueCode, seasonNo, rn)
    let errCount = 0
    try { const obj = JSON.parse(r.resultData); errCount = Number(obj?.errCount ?? 0) } catch {}
    const ok = r.resultCode === '00' && errCount === 0
    setActionMsg({ text: r.resultMessage || '완료', ok })
    fetchFixtures()
  }

  useEffect(() => {
    if (tab !== 'fixtures') return
    fetchFixtures()
  }, [tab, fetchFixtures])

  useEffect(() => {
    if (tab !== 'table') return
    let mounted = true
    setLoading(true); setErr('')
    apiLeagueTable(auth.token, leagueCode, seasonNo).then(r => {
      if (!mounted) return
      if (r.resultCode !== '00') { setErr(r.resultMessage || '조회 실패'); setLoading(false); return }
      try {
        const obj = JSON.parse(r.resultData)
        setStandings((obj.standings ?? []) as StandingRow[])
        setSeason(obj.season as SeasonInfo)
      } catch { setErr('응답 파싱 실패') } finally { setLoading(false) }
    }).catch(e => { setErr(e?.message ?? '네트워크 오류'); setLoading(false) })
    return () => { mounted = false }
  }, [tab, auth.token, leagueCode, seasonNo])

  // 라운드별 그룹핑
  const byRound: Record<number, FixtureRow[]> = {}
  fixtures.forEach(f => { (byRound[f.ROUND_NO] ??= []).push(f) })

  return (
    <div className="page-pad">
      <div className="sub-tabs">
        <button className={tab === 'fixtures' ? 'sub-tab active' : 'sub-tab'} onClick={() => setTab('fixtures')}>일정·결과</button>
        <button className={tab === 'table'    ? 'sub-tab active' : 'sub-tab'} onClick={() => setTab('table')}>리그 순위</button>
      </div>

      {tab === 'fixtures' && (
        <>
          <div className="round-pager">
            <button onClick={() => setRound(Math.max(1, round - 5))} disabled={round <= 1}>이전</button>
            <span>R{round} ~ R{Math.min(roundTotal, round + 4)}</span>
            <button onClick={() => setRound(Math.min(roundTotal - 4, round + 5))} disabled={round >= roundTotal - 4}>다음</button>
          </div>

          {actionMsg && <p className={actionMsg.ok ? 'msg ok' : 'msg'}>{actionMsg.text}</p>}
          {loading ? <p>불러오는 중…</p> : err ? <p className="msg">{err}</p> : (
            Object.keys(byRound).map(n => Number(n)).sort((a, b) => b - a).map(rn => {
              const hasPending = byRound[rn].some(f => f.STATUS === 0)
              return (
                <div key={rn} className="round-block">
                  <div className="round-head">
                    <h3>Round {rn}</h3>
                    {hasPending && (
                      <button className="btn-advance" onClick={() => handleAdvanceRound(rn)}>라운드 일괄 시뮬</button>
                    )}
                  </div>
                  <ul className="fixture-list">
                    {byRound[rn].map(f => {
                      const ended  = f.STATUS === 2
                      const live   = f.STATUS === 1
                      const isMine = (f.HOME_CLUB === myClubCode || f.AWAY_CLUB === myClubCode)
                      const myMark = isMine ? ' my' : ''
                      return (
                        <li key={f.FIXTURE_CODE} className={`fixture-row${myMark}`}>
                          <span className="date">{(f.MATCH_DATE || '').slice(0, 10)}</span>
                          <span className="home">{f.HOME_NAME}</span>
                          <span className="score">{ended || live ? `${f.HOME_GOAL ?? 0} : ${f.AWAY_GOAL ?? 0}` : 'vs'}</span>
                          <span className="away">{f.AWAY_NAME}</span>
                          {isMine && !ended && (
                            <button className="btn-live" onClick={() => handleStartLive(f.FIXTURE_CODE)}>
                              {live ? '라이브' : '경기 시뮬'}
                            </button>
                          )}
                          {ended && (
                            <button className="btn-result" onClick={() => onGoMatch(f.FIXTURE_CODE)}>결과</button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })
          )}
        </>
      )}

      {tab === 'table' && (
        loading ? <p>불러오는 중…</p> : err ? <p className="msg">{err}</p> : (
          <>
            {season && (
              <p className="season-info">
                {season.LEAGUE_NAME} · 시즌 {season.SEASON_NO} · R{season.CURRENT_ROUND}/{season.ROUND_TOTAL} · 시작 {(season.SEASON_START || '').slice(0, 10)}
              </p>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th><th>구단</th><th>P</th><th>W</th><th>D</th><th>L</th>
                  <th>GF</th><th>GA</th><th>GD</th><th>Pts</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((s, i) => (
                  <tr key={s.CLUB_CODE} className={s.CLUB_CODE === myClubCode ? 'my' : ''}>
                    <td>{i + 1}</td>
                    <td>{s.CLUB_NAME}</td>
                    <td>{s.PLAYED}</td>
                    <td>{s.WIN_CNT}</td>
                    <td>{s.DRAW_CNT}</td>
                    <td>{s.LOSE_CNT}</td>
                    <td>{s.GOAL_FOR}</td>
                    <td>{s.GOAL_AGAINST}</td>
                    <td>{s.GOAL_DIFF}</td>
                    <td className="pts">{s.POINTS}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      )}
    </div>
  )
}
